/**
 * AdminDisputes — admin-only dashboard at /admin/disputes.
 *
 * Lists every memorial-dispute submission, lets the operator:
 *   - filter by status (all / open / closed)
 *   - open a detail panel with the full description
 *   - close / reopen a dispute
 *   - reply via mailto: (pre-filled with subject + dispute ID)
 *
 * Access gated by /api/admin/me — non-admins see a graceful "no access" state.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLocale } from "@/lib/locale";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { ShieldAlert, ChevronLeft, Mail, Check, RotateCcw, Loader } from "lucide-react";

const FILTERS = [
    { key: "all",    labelKey: "admin.filter_all" },
    { key: "open",   labelKey: "admin.filter_open" },
    { key: "closed", labelKey: "admin.filter_closed" },
];

export default function AdminDisputes() {
    const { t } = useLocale();
    const [isAdmin, setIsAdmin] = useState(null);
    const [filter, setFilter] = useState("open");
    const [data, setData] = useState({ items: [], counts: { all: 0, open: 0, closed: 0 } });
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data: me } = await api.get("/admin/me");
                setIsAdmin(!!me?.is_admin);
            } catch (_e) {
                setIsAdmin(false);
            }
        })();
    }, []);

    const refresh = async () => {
        setLoading(true);
        try {
            const params = filter === "all" ? {} : { status: filter };
            const { data: resp } = await api.get("/admin/disputes", { params });
            setData(resp);
        } catch (_e) { /* noop */ }
        setLoading(false);
    };

    useEffect(() => {
        if (isAdmin === true) refresh();
    }, [isAdmin, filter]); // eslint-disable-line

    const changeStatus = async (id, status) => {
        setBusyId(id);
        try {
            await api.patch(`/admin/disputes/${id}`, { status });
            await refresh();
            if (selected?.dispute_id === id) {
                setSelected((s) => (s ? { ...s, status } : s));
            }
        } catch (_e) { /* noop */ }
        setBusyId("");
    };

    if (isAdmin === null) {
        return (
            <div className="min-h-screen flex items-center justify-center text-ink-textLow font-serif italic">
                <Loader size={16} className="animate-spin mr-2" /> …
            </div>
        );
    }

    if (isAdmin === false) {
        return (
            <div className="grain min-h-screen candle-halo">
                <Header minimal />
                <main className="max-w-xl mx-auto px-6 md:px-12 pt-24 pb-24 text-center" data-testid="admin-forbidden">
                    <ShieldAlert size={42} className="mx-auto text-ink-gold mb-6" />
                    <h1 className="h-serif text-3xl mb-3">{t("admin.not_authorized")}</h1>
                    <Link to="/dashboard" className="btn-ghost mt-6 inline-block">← {t("nav.presences")}</Link>
                </main>
            </div>
        );
    }

    return (
        <div className="grain min-h-screen candle-halo">
            <Header minimal />
            <main className="max-w-5xl mx-auto px-6 md:px-12 pt-12 pb-24" data-testid="admin-disputes">
                <div className="flex items-center gap-4 mb-10">
                    <BrandMark size={48} breathing={false} />
                    <div>
                        <p className="label-eyebrow text-ink-gold">{t("admin.eyebrow")}</p>
                        <h1 className="h-serif text-4xl md:text-5xl mt-1">{t("admin.title")}</h1>
                    </div>
                </div>

                <p className="text-ink-textMid text-lg mb-10 leading-relaxed max-w-2xl">
                    {t("admin.intro")}
                </p>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="admin-filters">
                    {FILTERS.map((f) => {
                        const active = filter === f.key;
                        return (
                            <button
                                key={f.key}
                                type="button"
                                onClick={() => { setFilter(f.key); setSelected(null); }}
                                className={`px-4 py-2 text-xs tracking-widest uppercase border rounded-sm transition-colors ${
                                    active
                                        ? "border-ink-gold text-ink-gold bg-ink-gold/10"
                                        : "border-ink-border text-ink-textMid hover:border-ink-textLow"
                                }`}
                                data-testid={`admin-filter-${f.key}`}
                            >
                                {t(f.labelKey)}
                                <span className="ml-2 text-ink-textLow">
                                    {data.counts?.[f.key] ?? 0}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Detail panel */}
                {selected && (
                    <div className="card-paper mb-10 animate-fade-in" data-testid="admin-detail-panel">
                        <button
                            type="button"
                            onClick={() => setSelected(null)}
                            className="text-sm text-ink-textMid hover:text-ink-textHigh mb-4 inline-flex items-center gap-1.5"
                            data-testid="admin-detail-back"
                        >
                            <ChevronLeft size={14} /> {t("admin.detail_back")}
                        </button>
                        <div className="space-y-4">
                            <div className="flex flex-wrap justify-between gap-4">
                                <div>
                                    <p className="font-serif text-xl text-ink-textHigh">{selected.complainant_name}</p>
                                    <p className="text-sm text-ink-textMid">{selected.complainant_email}</p>
                                    <p className="text-xs text-ink-textLow mt-1 font-mono">{selected.dispute_id}</p>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <StatusPill status={selected.status} t={t} />
                                    <a
                                        href={`mailto:${selected.complainant_email}?subject=${encodeURIComponent(`[Other Side] Your dispute ${selected.dispute_id}`)}`}
                                        className="inline-flex items-center gap-2 text-sm text-ink-gold hover:text-ink-goldHi"
                                        data-testid="admin-mailto"
                                    >
                                        <Mail size={14} /> {t("admin.action_respond")}
                                    </a>
                                </div>
                            </div>
                            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm border-t border-ink-border pt-4">
                                <Field label={t("admin.col_relation")} value={selected.relationship_claim} />
                                <Field label={t("admin.detail_country")} value={selected.country || "—"} />
                                <Field label={t("admin.col_date")} value={new Date(selected.created_at).toLocaleString()} />
                                <Field label={t("admin.detail_presence")} value={selected.presence_reference || "—"} />
                                <Field label={t("admin.detail_ip")} value={selected.ip || "—"} />
                                <Field label={t("admin.status_open")} value={selected.resend_id ? t("admin.detail_email_sent") : t("admin.detail_email_missed")} />
                            </dl>
                            <div>
                                <p className="label-eyebrow mb-2">{t("admin.detail_desc")}</p>
                                <p className="font-serif text-ink-textHigh whitespace-pre-wrap leading-relaxed" data-testid="admin-detail-description">
                                    {selected.description}
                                </p>
                            </div>
                            <div className="flex items-center gap-3 pt-2 border-t border-ink-border">
                                {selected.status === "open" ? (
                                    <button
                                        type="button"
                                        onClick={() => changeStatus(selected.dispute_id, "closed")}
                                        disabled={busyId === selected.dispute_id}
                                        className="btn-primary inline-flex items-center gap-2"
                                        data-testid="admin-close-btn"
                                    >
                                        <Check size={14} /> {t("admin.action_close")}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => changeStatus(selected.dispute_id, "open")}
                                        disabled={busyId === selected.dispute_id}
                                        className="btn-ghost inline-flex items-center gap-2"
                                        data-testid="admin-reopen-btn"
                                    >
                                        <RotateCcw size={14} /> {t("admin.action_reopen")}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* List */}
                {loading ? (
                    <p className="text-ink-textMid italic" data-testid="admin-loading">…</p>
                ) : data.items.length === 0 ? (
                    <p className="text-ink-textMid italic card-paper text-center py-8" data-testid="admin-empty">
                        {t("admin.empty")}
                    </p>
                ) : (
                    <div className="border border-ink-border rounded-sm overflow-hidden" data-testid="admin-list">
                        <table className="w-full text-sm">
                            <thead className="bg-ink-surface/50">
                                <tr className="text-ink-textMid text-left tracking-wider uppercase text-xs">
                                    <th className="px-4 py-3">{t("admin.col_date")}</th>
                                    <th className="px-4 py-3">{t("admin.col_name")}</th>
                                    <th className="px-4 py-3 hidden md:table-cell">{t("admin.col_relation")}</th>
                                    <th className="px-4 py-3 hidden md:table-cell">{t("admin.col_email")}</th>
                                    <th className="px-4 py-3">{t("admin.col_status")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((d) => (
                                    <tr
                                        key={d.dispute_id}
                                        onClick={() => setSelected(d)}
                                        className="border-t border-ink-border hover:bg-ink-surface/40 cursor-pointer transition-colors"
                                        data-testid={`admin-row-${d.dispute_id}`}
                                    >
                                        <td className="px-4 py-3 text-ink-textMid whitespace-nowrap">
                                            {new Date(d.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-4 py-3 text-ink-textHigh">{d.complainant_name}</td>
                                        <td className="px-4 py-3 text-ink-textMid hidden md:table-cell">{d.relationship_claim}</td>
                                        <td className="px-4 py-3 text-ink-textMid hidden md:table-cell">{d.complainant_email}</td>
                                        <td className="px-4 py-3"><StatusPill status={d.status} t={t} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
        </div>
    );
}

function StatusPill({ status, t }) {
    const isOpen = status === "open";
    return (
        <span
            className={`text-[11px] uppercase tracking-widest px-2 py-1 rounded-sm inline-block ${
                isOpen
                    ? "bg-amber-500/15 text-amber-200"
                    : "bg-ink-surface text-ink-textMid"
            }`}
            data-testid={`admin-status-${status}`}
        >
            {isOpen ? t("admin.status_open") : t("admin.status_closed")}
        </span>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <p className="label-eyebrow">{label}</p>
            <p className="text-ink-textHigh mt-1 break-words">{value}</p>
        </div>
    );
}
